import List from "./list";
import GalleryDisplay from "./gallery-display";
import {useContext, useRef, useState} from "react";
import {nanoid} from "nanoid/non-secure";
import FormContext from '../form-context';
import _get from 'lodash/get';
import GalleryUploadForm from './gallery-upload-form';
import {appendGalleryBatch} from './gallery-items';
import {getAcceptedFileTypes} from './uploader/uploader-utils';
import {CustomGallery as PhotoViewer, DefaultLayout} from 'react-photoswipe-gallery';
import PhotoSwipeUI from 'photoswipe/dist/photoswipe-ui-default';
import {createPortal} from 'react-dom';
import GalleryItemSummary from './gallery-item-summary';

const getSettings = (props) => {
    const categories = props.categories?.map(c => ({label: c.title, value: c.id}))

    return {
        sortable: true,
        labelFieldPath: "title",
        fieldsets: [
            {
                fields: [
                    categories?.length === 1 ? {
                        name: "category",
                        type: "hidden",
                        value: categories[0].value
                    } : null,
                    categories?.length > 1 ? {
                        label: "Category",
                        name: "category",
                        type: "combobox",
                        canCreate: false,
                        options: categories,
                    } : null,
                    {
                        label: "Caption",
                        type: "text",
                        name: "title",
                    },
                    {
                        label: "Text",
                        type: "rich-text",
                        name: "text"
                    },
                    {
                        label: "Image",
                        type: "image-uploader",
                        name: "image",
                    },
                    {
                        label: "Describe Image (Alt Text)",
                        description: "Text that is shown when an image doesn't load and also aids vision-impaired visitors and SEO",
                        type: "text",
                        name: "altText",
                        showsWhen: "image !== '' && image !== undefined"
                    },
                ].filter(f => !!f)
            }
        ]
    }
}

Gallery.WebsiteProductDisplayView = GalleryDisplay;

export default function Gallery(props) {
    const context = useContext(FormContext);
    const [view, setView] = useState('grid');
    const showThumbnails = context.layout === 'panel-style';
    const pickerRef = useRef(null);
    const viewerRef = useRef(null);
    const categoryRef = useRef(null);
    const latest = useRef({props, context});
    latest.current = {props, context};
    const settings = getSettings(props);
    const close = () => latest.current.context.setContext({showChildForm: false});
    const chooseImages = category => {
        categoryRef.current = category;
        pickerRef.current.click();
    };
    const addItems = () => chooseImages(props.categories?.length === 1 ? props.categories[0].id : undefined);

    return (
        <>
            <PhotoViewer layoutRef={viewerRef} ui={PhotoSwipeUI}
                         options={{shareEl: false, fullscreenEl: false, showHideOpacity: true}}>
                <List {...props} {...settings} allowClearAll={props.allowClearAll !== false}
                      className={[props.className, showThumbnails && 'gallery-editor'].filter(Boolean).join(' ')}
                      actionsInMenu={showThumbnails}
                      additionalAddActions={[{label: 'Add Multiple Items', onClick: addItems}]}
                      additionalListActions={[{label: view === 'grid' ? 'Switch To List View' : 'Switch To Grid View',
                          onClick: () => setView(current => current === 'grid' ? 'list' : 'grid')}]}
                      listClassName={showThumbnails ? `gallery-editor-items gallery-editor-${view}` : undefined}
                      itemClassName={showThumbnails ? `gallery-editor-item gallery-editor-${view}-item` : undefined}
                      axis={showThumbnails && view === 'grid' ? 'xy' : 'y'}
                      renderItemSummary={showThumbnails ? (item, index) =>
                          <GalleryItemSummary item={item} index={index} categories={props.categories}
                                              fileUploadSettings={props.fileUploadSettings}/> : undefined}/>
                {createPortal(<DefaultLayout ref={viewerRef}/>, document.body)}
            </PhotoViewer>
            <input ref={pickerRef} type="file" multiple accept={getAcceptedFileTypes({type: 'image-uploader'})}
                   className="hidden" disabled={props.disabled} aria-label="Choose Gallery Images"
                   onChange={event => {
                       const initialFiles = Array.from(event.target.files || []);
                       event.target.value = '';
                       if (!initialFiles.length) return;
                       const current = latest.current;
                       const fileUploadSettings = typeof current.props.fileUploadSettings === 'function'
                           ? current.props.fileUploadSettings() : current.props.fileUploadSettings || current.context.fileUploadSettings;
                       current.context.setContext({showChildForm: true, childForm: <GalleryUploadForm key={nanoid()}
                           initialFiles={initialFiles} category={categoryRef.current} categories={current.props.categories}
                           fileUploadSettings={fileUploadSettings} onCancel={close}
                           onDone={added => {
                               const {props: currentProps, context: currentContext} = latest.current;
                               added.forEach(item => currentProps.beforeItemAdd?.(item));
                               const positionName = `${currentProps.name}Positions`;
                               const result = appendGalleryBatch(_get(currentContext.values, currentProps.name), added,
                                   _get(currentContext.values, positionName), !!currentContext.original);
                               if (result.positions) currentProps.onFieldChange({target: pickerRef.current}, positionName, result.positions);
                               currentProps.onFieldChange({target: pickerRef.current}, currentProps.name, result.items);
                               close();
                           }}/>
                       });
                   }}/>
        </>
    );
};
